/** Placeholder block shown while content loads. */

import { memo, useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet, type StyleProp, type ViewStyle } from 'react-native';

import { colors, motion, radius } from '../../theme';

export interface SkeletonProps {
  width?: number | `${number}%`;
  height?: number;
  style?: StyleProp<ViewStyle>;
  /** Circular placeholders for avatars and step markers. */
  circle?: boolean;
}

export const Skeleton = memo(function Skeleton({
  width = '100%',
  height = 16,
  style,
  circle = false,
}: SkeletonProps) {
  const pulse = useRef(new Animated.Value(0.4)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: motion.duration.slow,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0.4,
          duration: motion.duration.slow,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [pulse]);

  return (
    <Animated.View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[
        styles.block,
        { width, height, opacity: pulse, borderRadius: circle ? height / 2 : radius.sm },
        style,
      ]}
    />
  );
});

const styles = StyleSheet.create({
  block: {
    backgroundColor: colors.bg.skeleton,
  },
});
